<template>
    <div class="header-bar w-full flex items-center justify-between px-32 py-16 relative" :style="{ backgroundColor: 'var(--color-primary)', color: 'var(--color-primary-content)' }">
        <!-- Logo on the left -->
        <WFBuildingLogo class="wf-building-logo"/>
        
        <!-- Language Flags in the center - Landscape mode -->
        <div class="header-center">
            <div class="language-flags-container">
                <button
                    v-for="lang in dropdownLanguages"
                    :key="lang.code"
                    @click="setLanguage(lang)"
                    class="flag-button"
                    :class="{ active: lang.code === currentLanguageCode }"
                    :title="lang.label"
                >
                    <span
                        class="flag-icon"
                        :style="{ backgroundImage: 'url(' + lang.flag + ')' }"
                    ></span>
                </button>
            </div>
        </div>
        
        <!-- Clock & Date on the right -->
        <div v-if="settings.showClock || settings.showDate" class="header-dt">
            <WFClock v-if="settings.showClock" class="p-0"/>
            <WFDate v-if="settings.showDate" />
        </div>
    </div>
</template>

<script>
import { WFBuildingLogo, WFClock } from '@3dwayfinder/wayfinder-vue-components'
import { mapState } from "vuex";

export default {
    name: 'LandscapeHeader',
    components: {
		WFBuildingLogo,
        WFClock,
    },
    data() {
        return {
            currentLanguageCode: "no",
            fallbackFlags: {
                no: "https://flagcdn.com/no.svg",
                en: "https://flagcdn.com/gb.svg",
                sv: "https://flagcdn.com/se.svg",
            },
            fallbackLanguages: [
                { code: "no", label: "Norsk" },
                { code: "en", label: "English" },
                { code: "sv", label: "Svenska" },
            ],
        }
    },
    computed: {
        ...mapState("wf", ["mobile"]),
        ...mapState({
            storeLanguages: (state) =>
                (state.wf && Array.isArray(state.wf.languages)
                    ? state.wf.languages
                    : []) || [],
            storeLanguageCode: (state) =>
                state.wf && state.wf.language ? state.wf.language : null,
        }),
        
        dropdownLanguages() {
            const fromStore =
                this.storeLanguages && this.storeLanguages.length
                    ? this.storeLanguages
                        .map((lang) => this.mapStoreLanguage(lang))
                        .filter((lang) => !!lang)
                    : [];

            const merged = new Map();

            this.fallbackLanguages.forEach((fallback) => {
                merged.set(fallback.code, {
                    ...fallback,
                    flag: this.fallbackFlags[fallback.code] || null,
                });
            });

            fromStore.forEach((lang) => {
                merged.set(lang.code, {
                    ...merged.get(lang.code),
                    ...lang,
                    flag: lang.flag || this.fallbackFlags[lang.code],
                });
            });

            return Array.from(merged.values());
        },
    },
    mounted() {
        const saved = localStorage.getItem("selectedLanguage");
        if (saved) {
            this.currentLanguageCode = saved.toLowerCase();
        } else if (this.storeLanguageCode) {
            this.currentLanguageCode = this.storeLanguageCode.toLowerCase();
        }
    },
    watch: {
        storeLanguageCode: {
            immediate: true,
            handler(newVal) {
                if (newVal) {
                    this.currentLanguageCode = newVal.toLowerCase();
                }
            },
        },
    },
    props: {
    settings: {
        type: Object,
        default: () => ({ showClock: true, showDate: true })
    }
    },
    methods: {
        mapStoreLanguage(lang) {
            const rawCode =
                (typeof lang.getName === "function" && lang.getName()) ||
                lang.name ||
                lang.code;

            if (!rawCode) return null;

            const code = rawCode.toLowerCase();
            const label =
                (typeof lang.getNativeName === "function" && lang.getNativeName()) ||
                lang.label ||
                lang.name ||
                rawCode.toUpperCase();

            return {
                code,
                label,
                flag: this.fallbackFlags[code] || null,
            };
        },
        
        setLanguage(lang) {
            if (!lang) return;

            const codeLower = (lang.code || "").toLowerCase();
            this.currentLanguageCode = codeLower;
            localStorage.setItem("selectedLanguage", codeLower);

            // Call Wayfinder setLanguage
            const wfInstance = this.$wf || this.$wayfinder || window.wayfinder;
            if (wfInstance && typeof wfInstance.setLanguage === "function") {
                wfInstance.setLanguage(codeLower);
            }
        },
    },
}
</script>

<style lang="postcss" scoped>
    .header-bar {
        height: 14rem;
        display: flex;
        align-items: center;
        justify-content: space-between;
        background-color: var(--th-primary);
    }
    
    .wf-building-logo {
        background-position: center left !important;
        background-size: contain !important;
        background-repeat: no-repeat !important;
        height: 80%;
        max-width: 30rem;
        min-width: 16rem;
        margin-left: 0;
        flex: 0 0 auto;
    }
    
    .header-center {
        display: flex;
        justify-content: center;
        align-items: center;
        flex: 1;
    }
    
    .language-flags-container {
        display: flex;
        gap: 1.6rem;
        align-items: center;
    }
    
    /* Flag buttons styling for landscape */
    .flag-button {
        width: 5.4rem;
        height: 4rem;
        padding: 0.4rem;
        border-radius: 0.8rem;
        background-color: transparent;
        border: 2px solid transparent;
        cursor: pointer;
        transition: all 0.2s ease;
        
    }
    
    .flag-button:hover {
        border-color: var(--th-accent);
    }
    
    .flag-button.active {
        border-color: transparent;
        background-color: transparent;
        transform: scale(1.15);
    }
    
    .flag-button.active .flag-icon {
        box-shadow: 0px 4px 8px 0px rgba(0, 0, 0, 0.2);
    }
    
    .flag-icon {
        display: block;
        width: 100%;
        height: 100%;
        background-size: cover;
        background-position: center;
        border-radius: 0.6rem;
        transition: all 0.2s ease;
    }
    
    .header-dt {
        text-align: right;
        flex-shrink: 0;
        flex: 0 0 auto;
    }
    
    .wf-clock {
        font-size: 4rem;
		font-weight: 500;
        padding: 0;
        margin-bottom: 0.5rem;
    }

    .wf-date {
        font-size: 2.4rem;
        font-weight: 500;
    }
</style>